'use client'

import { type ComponentType } from 'react';
import type { ResourceConfig } from '../interfaces/ResourceConfig';
import { ClientResourceProvider } from '../components/ClientResourceProvider';
import { useResourceConfig } from '../hooks/useResourceConfig';
import { mergeResourceConfig } from './mergeResourceConfig';

/**
 * @group Functions
 * 
 * @summary
 * <blockquote>Higher-order component for component-level resource configuration</blockquote>
 * 
 * @remarks
 * This HOC allows individual client components to define or extend their ResourceConfig.
 * It merges the component's config with the existing page/layout config, with component
 * config taking precedence for conflicting fields.
 * 
 * Use cases:
 * - Override reporting transport for specific components
 * - Add component-specific logging
 * - Fine-tune Sentry config per component
 * 
 * @param Component - The component to wrap
 * @param componentConfig - Partial ResourceConfig to merge with existing config
 * @returns A wrapped component with merged configuration
 * 
 * @example
 * ```typescript
 * 'use client';
 * 
 * import { ConfiguredComponent } from '@shared/config';
 * 
 * function MyComponent() {
 *   return <div>Component with custom config</div>;
 * }
 * 
 * export default ConfiguredComponent(MyComponent, {
 *   reporting: {
 *     development: {
 *       transport: 'sentry',
 *       samplingRate: 0.5
 *     }
 *   }
 * });
 * ```
 */
/**
 * @internal
 */
export function ConfiguredComponent<T extends ComponentType<any>>(
  Component: T,
  componentConfig: Partial<ResourceConfig>
): ComponentType<React.ComponentProps<T>> {
  
  return function WrappedComponent(props: React.ComponentProps<T>) {
    // Get existing config from page/layout
    const existingConfig = useResourceConfig();
    
    // Merge component config with existing config using centralized merge function
    // If no existing config, use component config as base (component config must have auth)
    const mergedConfig = existingConfig
      ? mergeResourceConfig(existingConfig, componentConfig)
      : (componentConfig as ResourceConfig); // Component config must be complete if no existing config
    
    // Wrap with ClientResourceProvider to update global config
    return (
      <ClientResourceProvider config={mergedConfig}>
        <Component {...props} />
      </ClientResourceProvider>
    );
  };
}

